import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Trash2, User, Mail, Phone, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

export default function SpeakersSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const [speakers, setSpeakers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    organization: '',
    bio: '',
    email: '',
    phone: '',
    special_requirements: '',
    session_title: '',
    session_time: '',
    photo_url: ''
  });

  useEffect(() => {
    loadSpeakers();
  }, [eventId]);

  const loadSpeakers = async () => {
    const { data } = await supabase
      .from('speakers')
      .select('*')
      .eq('event_id', eventId)
      .order('name');
    if (data) setSpeakers(data);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await supabase
        .from('speakers')
        .update(formData)
        .eq('id', editingId);
    } else {
      await supabase
        .from('speakers')
        .insert({ ...formData, event_id: eventId });
    }
    resetForm();
    loadSpeakers();
  };

  const deleteEntry = async (id: string) => {
    await supabase.from('speakers').delete().eq('id', id);
    setDeleteId(null);
    loadSpeakers();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      organization: '',
      bio: '',
      email: '',
      phone: '',
      special_requirements: '',
      session_title: '',
      session_time: '',
      photo_url: ''
    });
    setShowForm(false);
    setEditingId(null);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>, speakerId?: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const id = speakerId || editingId || crypto.randomUUID();
      const fileExt = file.name.split('.').pop();
      const filePath = `${id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('speaker-photos')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('speaker-photos')
        .getPublicUrl(filePath);

      setFormData({ ...formData, photo_url: publicUrl });

      if (speakerId) {
        await supabase
          .from('speakers')
          .update({ photo_url: publicUrl })
          .eq('id', speakerId);
        loadSpeakers();
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async (speakerId?: string) => {
    if (speakerId) {
      await supabase
        .from('speakers')
        .update({ photo_url: null })
        .eq('id', speakerId);
      loadSpeakers();
    } else {
      setFormData({ ...formData, photo_url: '' });
    }
  };

  const editEntry = (entry: any) => {
    setFormData(entry);
    setEditingId(entry.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Speakers</h2>
          <p className="text-zinc-500 mt-1">Speaker information and requirements</p>
        </div>
        {permissions.canEdit && (
          <Button onClick={() => setShowForm(!showForm)} className="bg-white text-zinc-900 hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />
            Add Speaker
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Edit' : 'Add'} Speaker
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-300">Photo</Label>
              <div className="mt-1.5 flex items-center gap-4">
                {formData.photo_url ? (
                  <div className="relative">
                    <img
                      src={formData.photo_url}
                      alt="Speaker"
                      className="w-24 h-24 rounded-full object-cover border-2 border-zinc-700"
                    />
                    {permissions.canEdit && (
                      <button
                        type="button"
                        onClick={() => removePhoto()}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-colors"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-full bg-zinc-800 border-2 border-dashed border-zinc-700 flex items-center justify-center">
                    <User className="w-10 h-10 text-zinc-600" />
                  </div>
                )}
                {permissions.canEdit && (
                  <div>
                    <input
                      type="file"
                      id="photo-upload"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e)}
                      className="hidden"
                      disabled={uploadingPhoto}
                    />
                    <label htmlFor="photo-upload">
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-zinc-400 hover:text-white cursor-pointer"
                        disabled={uploadingPhoto}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById('photo-upload')?.click();
                        }}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                    </label>
                    <p className="text-xs text-zinc-500 mt-1">Max 5MB, JPG or PNG</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-zinc-300">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="title" className="text-zinc-300">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., CEO, Director"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="organization" className="text-zinc-300">Organization</Label>
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label htmlFor="bio" className="text-zinc-300">Bio</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows={3}
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-zinc-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-zinc-300">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="session_title" className="text-zinc-300">Session Title</Label>
                <Input
                  id="session_title"
                  value={formData.session_title}
                  onChange={(e) => setFormData({ ...formData, session_title: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="session_time" className="text-zinc-300">Session Time</Label>
                <Input
                  id="session_time"
                  value={formData.session_time}
                  onChange={(e) => setFormData({ ...formData, session_time: e.target.value })}
                  placeholder="e.g., 2:00 PM - 3:00 PM"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="special_requirements" className="text-zinc-300">Special Requirements</Label>
              <Textarea
                id="special_requirements"
                value={formData.special_requirements}
                onChange={(e) => setFormData({ ...formData, special_requirements: e.target.value })}
                rows={2}
                placeholder="AV needs, dietary restrictions, accessibility requirements, etc."
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" className="bg-white text-zinc-900 hover:bg-zinc-200">
                {editingId ? 'Update' : 'Add'} Speaker
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm} className="text-zinc-400 hover:text-white">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-zinc-500">Loading...</div>
      ) : speakers.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-8 text-center">
          <p className="text-zinc-500">No speakers added yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {speakers.map((speaker) => (
            <div key={speaker.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6 hover:border-zinc-700/50 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  {speaker.photo_url ? (
                    <img
                      src={speaker.photo_url}
                      alt={speaker.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-zinc-700"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center border-2 border-zinc-700">
                      <User className="w-6 h-6 text-zinc-500" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-white">{speaker.name}</h3>
                    {speaker.title && (
                      <p className="text-sm text-zinc-400">{speaker.title}</p>
                    )}
                    {speaker.organization && (
                      <p className="text-sm text-zinc-500">{speaker.organization}</p>
                    )}
                  </div>
                </div>
                {permissions.canEdit && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => editEntry(speaker)}
                      className="text-zinc-400 hover:text-white h-8"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(speaker.id)}
                      className="text-zinc-400 hover:text-red-400 h-8 w-8"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {speaker.bio && (
                <p className="text-sm text-zinc-400 mb-3">{speaker.bio}</p>
              )}

              {speaker.session_title && (
                <div className="bg-zinc-800/50 rounded-lg p-3 mb-3">
                  <p className="text-sm font-medium text-white">{speaker.session_title}</p>
                  {speaker.session_time && (
                    <p className="text-xs text-zinc-500 mt-1">{speaker.session_time}</p>
                  )}
                </div>
              )}

              <div className="space-y-2 text-sm">
                {speaker.email && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Mail className="w-3.5 h-3.5" />
                    <a href={`mailto:${speaker.email}`} className="hover:text-white transition-colors">
                      {speaker.email}
                    </a>
                  </div>
                )}
                {speaker.phone && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Phone className="w-3.5 h-3.5" />
                    <a href={`tel:${speaker.phone}`} className="hover:text-white transition-colors">
                      {speaker.phone}
                    </a>
                  </div>
                )}
              </div>

              {speaker.special_requirements && (
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Special Requirements</p>
                  <p className="text-sm text-zinc-400">{speaker.special_requirements}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Speaker?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this speaker from the event.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteEntry(deleteId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
